import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import { api } from './api';
import { forgetPushDevice, syncPush } from './push';
import type { Me, Role } from './types';

interface AuthState {
  ready: boolean;
  session: Session | null;
  me: Me | null;
  meLoading: boolean;
  meError: unknown;
  hasRole: (...roles: Role[]) => boolean;
  isStaff: boolean;
  refreshMe: () => Promise<unknown>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const userId = session?.user.id;
  const meQuery = useQuery({
    queryKey: ['me', userId],
    queryFn: api.me,
    enabled: !!userId,
    staleTime: 60_000,
  });

  // Keep this phone's alerts registered to whoever is signed in.
  const signedInEmail = meQuery.data?.email;
  useEffect(() => {
    if (signedInEmail) void syncPush();
  }, [signedInEmail]);

  const value = useMemo<AuthState>(() => {
    const me = meQuery.data ?? null;
    const hasRole = (...roles: Role[]) => !!me && (me.roles.includes('admin') || roles.some((r) => me.roles.includes(r)));
    return {
      ready,
      session,
      me,
      meLoading: !!userId && meQuery.isPending,
      meError: meQuery.error,
      hasRole,
      isStaff: !!me && me.roles.length > 0,
      refreshMe: () => meQuery.refetch(),
      signOut: async () => {
        await forgetPushDevice();
        await supabase.auth.signOut();
        qc.clear();
        try { localStorage.removeItem('one10:dashboard'); } catch { /* storage unavailable */ }
      },
    };
  }, [ready, session, userId, meQuery, qc]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
